import { set } from 'date-fns';
import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import { uniq } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { type CSSProperties, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { RecommendationSource } from '~/api/Catalog';
import { MechanicType } from '~/api/ETA';
import { KYCStatus } from '~/api/KYCStatus';
import { company, DEFAULT_COMPANIES } from '~/company/Company';
import CartCard from '~/components/CartCard/CartCard';
import { CartDeliveryStatus } from '~/components/CartDeliveryStatus/CartDeliveryStatus';
import Icon from '~/components/Icon/Icon';
import InlineNotification from '~/components/InlineNotification';
import { ModalType } from '~/components/Modal/interface';
import Price from '~/components/Price';
import { Recommendations } from '~/components/Recommendations';
import Section from '~/components/Section';
import Text from '~/components/Text';
import { UI_BOTTOM_COMPONENTS } from '~/constants/variables';
import { VERIFICATION } from '~/constants/verification';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import styles from '~/pages/Cart/Cart.module.scss';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import {
  cartStore,
  catalogStore,
  checkoutStore,
  mainStore,
  orderStore,
  settingStore,
  userStore,
} from '~/stores';
import { StartVerificationLocation } from '~/stores/MainStore';
import { isProductBundle } from '~/types/Product';

import Checkbox from '../../components/CheckboxCustom/Checkbox';

import { CartCostItems } from './components/CartCostItems';
import SubtotalInfo from './components/Subtotal/SubtotalInfo';
import { useStickyWrapper } from './hooks';

const CartContent = () => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const navigate = useNavigate();
  const { isMobile } = useGlobal();
  const { timeFormat } = useTimeFormat();

  const openDeliveryModal = () => {
    openModal(ModalType.DeliveryInfo, 'cart');
  };

  const subtotalWrapperRef = useRef<HTMLDivElement | null>(null);
  const cartContentRef = useRef<HTMLDivElement | null>(null);
  const unauthorizedContentRef = useRef<HTMLDivElement | null>(null);

  const { style: stickyWrapperStyles } = useStickyWrapper({
    elementRef: subtotalWrapperRef,
    containerRef: cartContentRef,
    containerPaddingBottom: 120,
    containerPaddingTop: 30,
    headerGap: DEFAULT_COMPANIES.includes(company.name) ? 0 : 30,
  });

  const analyticsTrackedRef = useRef(false);

  useEffect(() => {
    if (!analyticsTrackedRef.current) {
      analyticsTrackedRef.current = true;
      analyticsService.track(AnalyticsEvent.VIEW_CART, {
        currency: orderStore.currency.toUpperCase(),
        value: mainStore.toFloat(cartStore.finalPrice),
        items: cartStore.cartForFirebase,
      });
      analyticsService.track(AnalyticsEvent.VIEW_CART_BI, {
        cart_id: undefined,
        in_opening_hours: false,
        products_amount: cartStore.cart.reduce(
          (sum, item) => sum + item.count,
          0,
        ),
        items_amount: cartStore.cart.length,
        price: cartStore.totalCartPrice.base,
        final_price: cartStore.finalPrice,
        eta_min: orderStore.etaCalculation?.duration.min || 0,
        eta_max: orderStore.etaCalculation?.duration.max || 0,
        delivery_fee: orderStore.fee.shippingPounds || 0,
        threshold: orderStore.fee.thresholdPounds || 0,
        is_surger: orderStore.etaCalculation?.highDemand || false,
      });
    }
    if (cartStore.cart.length) {
      const skus = cartStore.cart
        .map((cartItem) => {
          const isBundle = isProductBundle(cartItem);

          if (!isBundle) {
            return cartItem.sku;
          }

          return cartItem.offers.map((offer) => offer.sku);
        })
        .flat();

      catalogStore.getProductRecommendationDebounced(uniq(skus), [
        ...(company.showMatchingProducts_CartPage
          ? [RecommendationSource.CART]
          : []),
      ]);
    }

    return () => {
      catalogStore.clearProductRecommendations(RecommendationSource.CART);
    };
  }, []);

  const handleEmptyCart = () => {
    analyticsService.track(AnalyticsEvent.PURCHASE_CART_DELETED, {
      cart_id: undefined,
      products_amount: cartStore.cart.reduce(
        (sum, item) => sum + item.count,
        0,
      ),
      items_amount: cartStore.cart.length,
      price: cartStore.totalCartPrice.base,
      final_price: cartStore.finalPrice,
      eta_min: orderStore.etaCalculation?.duration.min ?? 0,
      eta_max: orderStore.etaCalculation?.duration.max ?? 0,
      delivery_fee: orderStore.fee.shippingPounds ?? 0,
      threshold: orderStore.fee.thresholdPounds ?? 0,
      is_surger: orderStore.etaCalculation?.highDemand ?? false,
    });
    cartStore.emptySelectedItemsFromCart();
  };

  const handleCheckout = () => {
    if (!userStore.isAuthorized) {
      openModal(ModalType.Auth);
      return;
    }

    if (
      userStore.isAuthorized &&
      userStore.personalData.kycStatus !== KYCStatus.Verified
    ) {
      mainStore.setStartVerificationLocation(StartVerificationLocation.CART);
      userStore
        .requestKYCSession(
          `${window.location.origin}${window.location.pathname}`,
        )
        .then(({ data }) => {
          const shouldOpenInAppBrowser =
            mainStore.isRN &&
            Number(mainStore.appVersion.replaceAll('.', '')) >= 1029;

          if (shouldOpenInAppBrowser) {
            mainStore.sendToRN('openInAppBrowser', { url: data.url });
          } else {
            window.location.href = data.url;
          }
        })
        .catch(() => void 0);
      return;
    }
  };

  const handleNavigateToCheckout = async () => {
    const cartCountChanged = await cartStore.calculateCart();

    if (cartCountChanged) {
      return;
    }

    navigate('/checkout');
  };

  const parseTime = (time?: string) => {
    if (!time) {
      return '';
    }

    const [hours, minutes] = time.split(':');

    const result = format(
      set(Date.now(), { hours: +hours!, minutes: +minutes! }),
      timeFormat,
    );

    if (result === '23:59') {
      return t('midnight');
    }
    return result;
  };

  const isLoading =
    orderStore.isLoading ||
    orderStore.isETALoading ||
    cartStore.calculationProcess.isLoading;

  const hasAgeRestrictionError = checkoutStore.hasAgeRestrictionError;
  const ageRestrictionErrorType = checkoutStore.ageRestrictionErrorType;

  const expressMechanic =
    orderStore.etaCalculation?.warehouse?.availability?.deliveryMechanics?.[
      MechanicType.ON_DEMAND
    ];
  const isWarehouseClosed =
    orderStore.etaCalculation?.warehouse.availability.availableNow === false;

  const expressDelivery = orderStore.expressDelivery;
  const deliveryScheduleLimits = orderStore.slotDeliverySchedule;
  const warehouseAvailability = orderStore.warehouseAvailability;

  const showExpressUnavailableBlock =
    !!expressMechanic && !expressMechanic.availableNow;

  const renderMobileGoToCheckoutButton = () => {
    const goToCheckout = (
      <button
        type="button"
        onClick={handleNavigateToCheckout}
        className={classes([
          'button _primary _med',
          (cartStore.selectedCartItems.length === 0 ||
            isLoading ||
            isWarehouseClosed ||
            hasAgeRestrictionError) &&
            '_disabled',
        ])}
      >
        {t('goToCheckout')}
        {isLoading ? (
          <span className="spinner _white" />
        ) : (
          cartStore.selectedCartItems.length > 0 && (
            <Price price={cartStore.totalCartPrice.paidWithDiscount} />
          )
        )}
      </button>
    );

    // TODO: hide temporaryly in cart - JS-7631
    // if (
    //   (!company.needVerification ||
    //     (userStore.isAuthorized &&
    //       userStore.personalData.kycStatus === KYCStatus.Verified)) &&
    //   cartStore.totalCartPrice.minimal_order_value_error?.length &&
    //   cartStore.totalCartPrice.minimal_order_value_error[0]
    // ) {
    //   return (
    //     <>
    //       <div
    //         onClick={handleCheckout}
    //         className={classes([
    //           'button _primary _med _disabled _center',
    //           styles.subtotalInfo__button,
    //         ])}
    //       >
    //         {t('errors:minCartError', {
    //           amount: formatPriceWithCurrency(
    //             cartStore.totalCartPrice.minimal_order_value_error[0]
    //               ?.minimalOrderValue / 100,
    //           ),
    //         })}
    //         <br />
    //         {t('errors:minCartErrorDetails', {
    //           amount: formatPriceWithCurrency(
    //             (cartStore.totalCartPrice.minimal_order_value_error[0]
    //               ?.minimalOrderValue -
    //               cartStore.totalCartPrice.minimal_order_value_error[0]
    //                 ?.orderPriceWithoutDelivery) /
    //               100,
    //           ),
    //         })}
    //       </div>
    //     </>
    //   );
    // }
    if (!company.needVerification && !userStore.isAuthorized) {
      return (
        <>
          <div
            onClick={handleCheckout}
            className="button _primary subtotal-info__button _med _center"
          >
            {t('logInToCheckout')}
          </div>
          {company.showCartAdditionalTextMobile && (
            <Text size="12" weight="500" className="subtotal-info__additional">
              {t('phrases:verifyIdDescription')}
            </Text>
          )}
        </>
      );
    }

    if (!company.needVerification && userStore.isAuthorized) {
      return goToCheckout;
    }
    switch (userStore.personalData.kycStatus) {
      case KYCStatus.Verified:
        return goToCheckout;
      case KYCStatus.InProgress:
      case KYCStatus.ResubmissionRequested:
      case KYCStatus.Started: {
        const statusConfig = VERIFICATION[userStore.personalData.kycStatus];
        const isDisabled =
          userStore.personalData.kycStatus === KYCStatus.InProgress &&
          '_disabled';

        return (
          <>
            <div
              onClick={handleCheckout}
              className={`button _primary subtotal-info__button _med _center ${isDisabled}`}
            >
              {statusConfig.title}
            </div>
            <Text size="12" weight="500" className="subtotal-info__additional">
              {statusConfig.description || ''}
            </Text>
          </>
        );
      }
      default:
        return (
          <>
            <div
              onClick={handleCheckout}
              className="button _primary subtotal-info__button _med _center"
            >
              {userStore.isAuthorized ? t('verifyId') : t('logInToCheckout')}
            </div>
            <Text size="12" weight="500" className="subtotal-info__additional">
              {t('phrases:verifyIdDescription')}
            </Text>
          </>
        );
    }
  };

  const matchingProducts =
    catalogStore.productRecommendations[RecommendationSource.CART];
  const hasRecommendations = Boolean(matchingProducts?.data.length);

  const recommendationsBlock = catalogStore.productRecommendations[
    RecommendationSource.CART
  ]?.data.length ? (
    <div
      style={
        {
          '--value': `${unauthorizedContentRef?.current?.offsetHeight || 0}px`,
        } as CSSProperties
      }
      className={
        hasRecommendations || catalogStore.productRecommendationsMeta.isLoading
          ? styles.recommendationsWr
          : undefined
      }
    >
      <Recommendations
        className="carousel-scroll1"
        source={RecommendationSource.CART}
      />
    </div>
  ) : null;

  return (
    <>
      <div
        className={classes([
          'cart-content__container',
          userStore?.personalData?.kycStatus !== KYCStatus.Verified &&
            'cart-content__not-verified',
          hasRecommendations && styles.hasRecommendations,
        ])}
        ref={cartContentRef}
      >
        <div data-company={company.name} className="content-layout__contents">
          <Link
            data-link="continue-shopping"
            className="button _no-color _no-padding continue-shopping"
            to="/"
          >
            <Icon type="chevron" size={24} className="icon__rotate-90" />
            <p className="continue-shopping__text">{t('continueShopping')}</p>
          </Link>
          {(showExpressUnavailableBlock || isWarehouseClosed) &&
            (deliveryScheduleLimits || expressDelivery) && (
              <CartDeliveryStatus>
                <CartDeliveryStatus.Image src={company.images.time} />
                <CartDeliveryStatus.Content>
                  <CartDeliveryStatus.Text>
                    {isWarehouseClosed ? (
                      warehouseAvailability && (
                        <CartDeliveryStatus.Header isAllHeader>
                          <Trans
                            i18nKey="warehouseUnavailableTime"
                            values={{
                              time: parseTime(warehouseAvailability.opening),
                            }}
                            t={t}
                          />
                        </CartDeliveryStatus.Header>
                      )
                    ) : (
                      <>
                        {expressDelivery && (
                          <CartDeliveryStatus.Header>
                            <>
                              <i>{t('expressDelivery')}:</i>
                              {isMobile ? <br /> : ' '}
                              {t('deliveryAvailableTime', {
                                timeFrom: parseTime(expressDelivery.opening),
                                timeTo: parseTime(expressDelivery.closing),
                              })}
                            </>
                          </CartDeliveryStatus.Header>
                        )}
                        {deliveryScheduleLimits && (
                          <CartDeliveryStatus.Description>
                            <i>{t('standardDelivery')}:</i>
                            {isMobile ? <br /> : ' '}
                            {t(
                              deliveryScheduleLimits.opening
                                ? 'deliveryAvailableTime'
                                : 'deliveryAvailableUntilTime',
                              {
                                timeFrom: parseTime(
                                  deliveryScheduleLimits.opening,
                                ),
                                timeTo: parseTime(
                                  deliveryScheduleLimits.closing,
                                ),
                              },
                            )}
                          </CartDeliveryStatus.Description>
                        )}
                      </>
                    )}
                  </CartDeliveryStatus.Text>
                  {company.match({
                    citydrinks: (
                      <CartDeliveryStatus.Info onClick={openDeliveryModal}>
                        <Icon type="info" size={16} />
                        {t('learnMore')}
                      </CartDeliveryStatus.Info>
                    ),
                    default: null,
                  })}
                </CartDeliveryStatus.Content>
              </CartDeliveryStatus>
            )}

          {isMobile && cartStore.cart.length > 0 && recommendationsBlock}

          <div className="cart-header">
            <p className="cart-header__title">
              {t('myCart')}
              <p className="cart-header__title__count">
                {t('phrases:itemsCount', {
                  count: cartStore.selectedItemsTotalCount,
                })}
              </p>
            </p>

            {hasAgeRestrictionError && (
              <InlineNotification
                variant="error"
                title={
                  ageRestrictionErrorType === 'no-date'
                    ? t('ageRestriction:titleFillDateOfBirth')
                    : t('ageRestriction:title')
                }
                description={(() => {
                  const minAge =
                    settingStore.getMinAgeForItems(
                      cartStore.selectedCartItems,
                    ) || settingStore.maxMinAge;

                  if (ageRestrictionErrorType === 'unauthorized') {
                    return t('ageRestriction:unauthorizedMessage', { minAge });
                  }
                  if (ageRestrictionErrorType === 'no-date') {
                    return t('ageRestriction:noDateMessage', { minAge });
                  }
                  return t('ageRestriction:underageMessage', { minAge });
                })()}
              >
                {ageRestrictionErrorType === 'no-date' && (
                  <button
                    type="button"
                    className="button _white _bordered _xs"
                    onClick={() => {
                      openModal(ModalType.BirthDate);
                    }}
                  >
                    {t('ageRestriction:backToDataEntry')}
                  </button>
                )}
              </InlineNotification>
            )}
            <div className="cart-header__buttons">
              <button
                data-button="delete"
                className="cart-header__btn button _no-color"
                onClick={handleEmptyCart}
              >
                <Icon size={24} type="close" />
                <Text size="15" weight="500">
                  {t('deleteSelected')}{' '}
                  {company.showCartCoutToRemove &&
                    `(${cartStore.selectedItemsTotalCount})`}
                </Text>
              </button>

              <Checkbox
                attributeName="select-all"
                onChange={cartStore.handleSelectAllCartItems}
                checked={cartStore.isSelectedAll}
              >
                <div className="cart-header__selectAll">{t('selectAll')}</div>
              </Checkbox>
            </div>
          </div>

          <Section className="cart-content">
            <ul className="cart-content__list">
              {cartStore.cart.map((offer) => (
                <li className="cart-content__list-item" key={offer.sku}>
                  <CartCard item={offer} showCheckbox size="m" />
                </li>
              ))}
            </ul>

            {company.showCartMobileSubtotal ? (
              <div className={styles.mobileSubtotalInfoWr}>
                <CartCostItems />
              </div>
            ) : (
              <div className="cart-content__subtotal">
                <p className="cart-content__subtotal__title">{t('subtotal')}</p>
                <Price
                  price={cartStore.totalCartPrice.paidWithoutDiscount}
                  classNameCurrentPrice="cart-content__subtotal__price"
                />
              </div>
            )}
          </Section>
        </div>

        <SubtotalInfo
          onNavigateToCheckout={handleNavigateToCheckout}
          onCheckout={handleCheckout}
          isLoading={isLoading}
          isWarehouseClosed={isWarehouseClosed}
          subtotalWrapperRef={subtotalWrapperRef}
          stickyWrapperStyles={stickyWrapperStyles}
        />

        {!company.newDesignSystemEnabled && (
          <div
            ref={unauthorizedContentRef}
            className="mobile-bottom-button-container"
            id={UI_BOTTOM_COMPONENTS.CTA}
          >
            {renderMobileGoToCheckoutButton()}
          </div>
        )}
      </div>

      {!isMobile && recommendationsBlock}
    </>
  );
};

export default observer(CartContent);
